/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { AccessTime } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert,
    Box,
    Chip,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { locationsAPI } from 'api/locationsAPI';
import { JourneyType } from 'app/constants/Booking/BookingConstants';
import DisplayError from 'components/Common/DisplayError/DisplayError';
import SectionHeader from 'components/Common/SectionHeader';
import Checkbox from 'components/RouteWizard/Checkbox';
import { StepComponentProps } from 'components/RouteWizard/RouteWizard';
import { Select } from 'components/RouteWizard/Select';
import { useWizard } from 'components/RouteWizard/Wizard';
import { addDays, isAfter } from 'date-fns';
import useFormState from 'hooks/useFormState';
import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import AdditionalServices from '../JourneyPlan/AdditionalServices';
import AddressSearch from '../JourneyPlan/AddressSearch';
import { formatDateAndTime } from '../JourneyPlan/defaultValues';
import DateTimeSelect from '../JourneyPlan/LegacyDateTimeSelect';
import Stops from '../JourneyPlan/Stops';
import { asDirectedHours, waitTimes } from '../JourneyPlan/timeOptions';

interface Props extends StepComponentProps {
  callbackPreview?: (value: any) => void
  formMethods: any
}

const JourneyPlan = ({
  formMethods,
  initialValues,
  callbackPreview,
}: Props) => {
  const [enabled, setEnabled] = React.useState(false)
  const journeyTypeName = useFormState('journeyTypeName', formMethods.current)

  const pickup = useFormState('outbound.pickup', formMethods.current)
  const dropOff =
    useFormState('outbound.dropOff', formMethods.current) ||
    initialValues?.outbound?.dropOff
  const outbound =
    useFormState('outbound.when', formMethods.current) ||
    initialValues?.outbound?.when
  const outboundWhen = formatDateAndTime(outbound)
  const inboundWhen = formatDateAndTime(
    useFormState('inbound.when', formMethods.current),
  )

  const isImmediateJourney = useFormState(
    'isImmediateJourney',
    formMethods.current,
  )
  const stops = useFormState('additionalStops', formMethods.current, true)

  const setOutboundToday = () => {
    const today = new Date()
    if (outboundWhen) {
      today.setHours(outboundWhen?.getHours(), outboundWhen?.getMinutes())
    }
    formMethods.current?.setValue('outbound.when', today)
  }

  const setOutboundTomorrow = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    if (outboundWhen) {
      tomorrow.setHours(outboundWhen?.getHours(), outboundWhen?.getMinutes())
    }
    formMethods.current?.setValue('outbound.when', tomorrow)
  }

  const setInboundSameDay = () => {
    const sameDay = outboundWhen ? new Date(outboundWhen) : new Date()
    if (inboundWhen) {
      sameDay.setHours(inboundWhen?.getHours(), inboundWhen?.getMinutes())
    }
    formMethods.current?.setValue('inbound.when', sameDay)
  }

  const setInboundNextDay = () => {
    const nextDay = outboundWhen ? new Date(outboundWhen) : new Date()
    nextDay.setDate(nextDay.getDate() + 1)
    if (inboundWhen) {
      nextDay.setHours(inboundWhen?.getHours(), inboundWhen?.getMinutes())
    }
    formMethods.current?.setValue('inbound.when', nextDay)
  }

  const populateFixedRoute = (fixedRoute: any) => {
    formMethods?.current?.setValue('outbound.pickup', fixedRoute.pickup)
    formMethods?.current?.setValue('outbound.dropOff', fixedRoute.dropOff)
  }

  const { data } = useWizard()

  const preview = {
    ...data.preview,
    pickup,
    dropOff,
    when: outboundWhen,
    additionalStops: stops,
    isImmediateJourney:
      journeyTypeName == JourneyType.ONE_WAY && isImmediateJourney,
  }

  // const outsideTimeZone = useQuery({
  //   queryKey: ['getTimeZoneQuote'],
  //   queryFn: async () => {
  //     locationsAPI.getTimeZone(
  //       formMethods.current?.getValues('outbound.pickup.latitude'),
  //       formMethods.current?.getValues('outbound.pickup.longitude'),
  //     )
  //   },
  //   enabled: true,
  //   staleTime: 0,
  // })

  const countryCode = formMethods.current?.getValues(
    'outbound.pickup.countryCode',
  )
  const latAndLong =
    formMethods.current?.getValues('outbound.pickup.latitude') !== undefined &&
    formMethods.current?.getValues('outbound.pickup.longitude') !== undefined
  useQuery({
    queryKey: ['getTimeZoneQuote'],
    queryFn: async () => {
      return locationsAPI.getTimeZone(
        formMethods.current?.getValues('outbound.pickup.latitude'),
        formMethods.current?.getValues('outbound.pickup.longitude'),
      )
    },
    enabled: Boolean((!countryCode || countryCode !== 'GBR') && latAndLong),
    staleTime: 0,
    onSuccess: (data) => {
      formMethods.current?.setValue('outbound.timeZone', data?.data.timeZone)
    },
  })

  useEffect(() => {
    if (
      formMethods.current?.getValues('outbound.pickup.countryCode') === 'GBR'
    ) {
      console.log('pickup is GBR')
      formMethods.current?.setValue('outbound.timeZone', 'Europe/London')
    } else {
      console.log('pickup is not GBR')
    }
  }, [formMethods.current?.getValues('outbound.pickup.countryCode')])

  useEffect(() => {
    if (callbackPreview) {
      callbackPreview(preview)
    }
  }, [
    pickup,
    dropOff,
    outboundWhen,
    stops,
    journeyTypeName,
    JourneyType.ONE_WAY,
    isImmediateJourney,
  ])

  useEffect(() => {
    if (!journeyTypeName) {
      formMethods.current?.setValue(
        'journeyTypeName',
        initialValues?.journeyTypeName || JourneyType.ONE_WAY,
      )
    }
  }, [])

  useEffect(() => {
    let triggerQuery = formMethods.current?.getValues(
      'outbound.pickup.countryCode',
    )
    if (triggerQuery != undefined && triggerQuery != 'GBR') {
      setEnabled(true)
    } else {
      formMethods.current?.setValue('outbound.timeZone', 'Europe/London')
      setEnabled(false)
    }
  }, [formMethods.current?.getValues('outbound.pickup')])

  return (
    <Box
      component="div"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: '20px',
        marginBottom: '30px',
        float: 'right',
      }}
    >
      <AddressSearch
        name="outbound.pickup"
        key={JSON.stringify(formMethods?.current?.getValues('outbound.pickup'))}
        id="pick-up"
        label="Pick up"
        clientId={formMethods.current?.getValues('clientId')}
        populateFixedRoute={populateFixedRoute}
      />
      {formMethods.current?.formState?.errors?.outbound?.pickup && (
        <DisplayError
          message={
            formMethods.current?.formState?.errors?.outbound?.pickup.message
          }
          sx={{
            marginTop: '-10px',
            marginLeft: '125px',
          }}
        />
      )}

      <AddressSearch
        name="outbound.dropOff"
        key={JSON.stringify(
          formMethods?.current?.getValues('outbound.dropOff'),
        )}
        id="drop-off"
        label="Drop Off"
        clientId={formMethods.current?.getValues('clientId')}
        populateFixedRoute={populateFixedRoute}
      />
      {formMethods.current?.formState?.errors?.outbound?.dropOff && (
        <DisplayError
          message={
            formMethods.current?.formState?.errors?.outbound?.dropOff.message
          }
          sx={{
            marginTop: '-10px',
            marginLeft: '125px',
          }}
        />
      )}

      {journeyTypeName === JourneyType.AS_DIRECTED && (
        <SectionHeader
          subtitle="Note: Drop Off is not mandatory for an hourly trip"
          containerStyles={{
            alignSelf: 'left',
            marginLeft: '120px',
          }}
        />
      )}

      <Stops
        name="additionalStops"
        clientId={formMethods.current?.getValues('clientId')}
      />

      <Controller
        control={formMethods.current?.control}
        name={'journeyTypeName'}
        render={({ field: { ...fieldProps } }) => {
          return (
            <RadioGroup
              aria-label="journeytype"
              {...fieldProps}
              sx={{
                backgroundColor: '#f4f4f4',
                fontSize: '20px',
                color: '#878787',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-evenly',
                marginTop: '20px',
                padding: '10px 20px',
              }}
            >
              <FormControlLabel
                value={JourneyType.ONE_WAY}
                control={
                  <Radio checked={journeyTypeName === JourneyType.ONE_WAY} />
                }
                label="One Way"
              />
              <FormControlLabel
                value={JourneyType.FLEXIBLE}
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '5px',
                      alignItems: 'center',
                    }}
                  >
                    <Typography>Flexible</Typography>
                    {!isAfter(
                      new Date(),
                      addDays(new Date('2025-08-15'), 30),
                    ) && (
                      <Chip
                        label={'new'}
                        size="small"
                        color="success"
                        sx={{ position: 'relative', top: '-10px' }}
                      ></Chip>
                    )}
                  </Box>
                }
              />
              <FormControlLabel
                value={JourneyType.RETURN}
                control={
                  <Radio checked={journeyTypeName === JourneyType.RETURN} />
                }
                label="Return"
              />
              <FormControlLabel
                value={JourneyType.AS_DIRECTED}
                control={
                  <Radio
                    checked={journeyTypeName === JourneyType.AS_DIRECTED}
                  />
                }
                label="Hourly Trip"
              />
            </RadioGroup>
          )
        }}
      />
      {journeyTypeName === JourneyType.FLEXIBLE && (
        <Alert icon={<AccessTime />} color="info" sx={{ marginTop: '20px' }}>
          Flexible bookings let you request your car when you’re ready. Just
          give us your expected departure time - it’s okay if you’re early or
          late. Your booking stays active for 12 hours after that time.
        </Alert>
      )}
      <Box
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '20px',
        }}
      >
        <Grid container justifyContent="center">
          <Grid
            item
            xs={6}
            sx={
              journeyTypeName === JourneyType.RETURN
                ? { paddingRight: '10px' }
                : undefined
            }
          >
            <DateTimeSelect
              namePrefix="outbound"
              title={
                journeyTypeName === JourneyType.FLEXIBLE
                  ? 'expected departure'
                  : 'out'
              }
              leftQuickButtonLabel="today"
              leftOnClick={setOutboundToday}
              rightQuickButtonLabel="tomorrow"
              rightOnClick={setOutboundTomorrow}
              disabled={
                isImmediateJourney && journeyTypeName === JourneyType.ONE_WAY
              }
            />
          </Grid>
          {journeyTypeName === JourneyType.RETURN && (
            <Grid item xs={6} sx={{ paddingLeft: '10px' }}>
              <DateTimeSelect
                namePrefix="inbound"
                title="return"
                leftQuickButtonLabel="same day"
                leftOnClick={setInboundSameDay}
                rightQuickButtonLabel="next day"
                rightOnClick={setInboundNextDay}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      {journeyTypeName === JourneyType.AS_DIRECTED && (
        <Grid container justifyContent="center">
          <Grid item xs={6} sx={{ paddingRight: '10px' }}>
            <Box
              display="flex"
              sx={{ marginTop: '20px' }}
              justifyContent="flex-start"
            >
              <SectionHeader
                title={'Hourly Trip'}
                containerStyles={{
                  alignSelf: 'center',
                }}
              />
              <Tooltip title="Select the amount of time for trip and your driver will remain on standby for the duration.">
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ paddingLeft: '5px' }}
                />
              </Tooltip>
            </Box>
            <Box
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              <Select
                name={`directedJourneyTime`}
                options={
                  asDirectedHours?.reduce<Record<string, string>>(
                    (acc, item) => {
                      acc[item.value] = item.label
                      return acc
                    },
                    {},
                  ) || {}
                }
                sx={{ flexGrow: 1, marginRight: '10px' }}
              />
            </Box>
          </Grid>
        </Grid>
      )}
      {journeyTypeName === JourneyType.WAIT_AND_RETURN && (
        <Grid item xs={6}>
          <SectionHeader
            title={'Waiting Time'}
            containerStyles={{
              alignSelf: 'center',
              marginTop: '20px',
            }}
          />
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: '10px',
            }}
          >
            <Select
              name={`waitHours`}
              options={
                waitTimes
                  ?.filter((option) => option?.type === 'hours')
                  ?.reduce<Record<string, string>>((acc, item) => {
                    acc[item.label] = item.value
                    return acc
                  }, {}) || {}
              }
              label="Hours"
              sx={{ flexGrow: 1 }}
            />

            <Select
              name={`waitMinutes`}
              options={
                waitTimes
                  ?.filter((option) => option?.type === 'minutes')
                  ?.reduce<Record<string, string>>((acc, item) => {
                    acc[item.label] = item.value
                    return acc
                  }, {}) || {}
              }
              label="Minutes"
              sx={{ flexGrow: 1 }}
            />
          </Box>
        </Grid>
      )}
      {journeyTypeName === JourneyType.ONE_WAY && (
        <Grid container justifyContent="center">
          <Grid item xs={6} sx={{ paddingRight: '10px' }}>
            <Checkbox name="isImmediateJourney" label="ASAP" />
          </Grid>
        </Grid>
      )}
      <Grid container justifyContent="center">
        <Grid item xs={journeyTypeName === JourneyType.RETURN ? 12 : 6}>
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: '20px',
            }}
          >
            <AdditionalServices namePrefix="outbound" />
            {journeyTypeName === JourneyType.RETURN && (
              <AdditionalServices namePrefix="inbound" />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default JourneyPlan
